import { type ReactNode } from 'react';
import clsx from 'clsx';
import { type Placement } from 'react-aria';
import {
  Tooltip as AriaTooltip,
  TooltipTrigger,
  Button,
  OverlayArrow,
} from 'react-aria-components';

import { Question } from '@hungryroot/ui-kit';
import { useTooltipManager } from './useTooltipManager';
import styles from './Tooltip.module.css';

type TooltipTheme = 'dark' | 'light';

interface Props {
  children?: ReactNode;
  testid?: string;
  content: ReactNode;
  placement?: Placement;
  trigger?: 'manual' | 'click' | 'hover';
  maxWidth?: number | string;
  className?: string;
  theme?: TooltipTheme;
  ariaLabel?: string;
  showOnCreate?: boolean;
  scrollDismiss?: boolean;
  dismissClickTarget?: string;
  dismissDelay?: number;
  hideOnClick?: boolean;
  containerClassName?: string;
  onShown?: () => void;
}

const defaultIcon = (
  <div className={styles.default}>
    <Question />
  </div>
);

export function Tooltip({
  children = defaultIcon,
  maxWidth = 200,
  placement = 'bottom start',
  className = '',
  theme = 'dark',
  ariaLabel,
  showOnCreate = false,
  scrollDismiss = true,
  dismissDelay = undefined,
  ...props
}: Props) {
  const {
    content,
    testid,
    trigger,
    hideOnClick,
    containerClassName,
    dismissClickTarget,
    onShown,
  } = props;

  const tooltipClassName =
    theme === 'light' ? styles['tooltip-light'] : styles.tooltip;

  const { isOpen, setIsOpen, containerRef } = useTooltipManager({
    showOnCreate,
    scrollDismiss,
    dismissDelay,
    dismissClickTarget,
    onShown,
    trigger,
    hideOnClick,
  });

  return (
    <TooltipTrigger delay={100} isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button
        className={clsx(styles['tooltip-trigger'], containerClassName)}
        ref={containerRef}
        data-testid={testid}
        aria-label={ariaLabel}
      >
        {children}
      </Button>
      <AriaTooltip
        className={clsx(tooltipClassName, className)}
        placement={placement}
        style={{ maxWidth }}
        offset={10}
      >
        <OverlayArrow>
          {theme === 'light' ? (
            <svg
              width={24}
              height={24}
              viewBox="0 0 24 24"
              className={styles['tooltip-arrow-light']}
            >
              <path
                className={styles['tooltip-arrow-fill']}
                d="M0 0 L12 12 L24 0 Z"
              />
              <path className={styles['tooltip-arrow-edge']} d="M1 0 L12 12" />
              <path className={styles['tooltip-arrow-edge']} d="M23 0 L12 12" />
            </svg>
          ) : (
            <svg width={24} height={24} viewBox="0 0 24 24">
              <path d="M0 0 L12 12 L24 0" />
            </svg>
          )}
        </OverlayArrow>
        {content}
      </AriaTooltip>
    </TooltipTrigger>
  );
}
